// contract-manager/client/pages/companies/edit.jsx
import { StatusSwitcher } from '@/components/status-switcher'
import { useAuthorization } from '@/hooks'
import { PageLayout } from '@/layouts/page-layout'
import { list } from '@/router/utils'
import { confirm } from '@/shared/components/confirmation/confirmation.util'
import { useGetCompanyQuery } from '@/store/services/company.service'
import { Button } from '@material-tailwind/react'
import { HasAccess } from '@permify/react-role'
import { Form, Formik } from 'formik'
import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Link,
  useNavigation,
  useParams,
  useSubmit
} from 'react-router-dom'
import {
  CommercialInformationForm,
  LegalInformationForm,
  SafetyInformationForm,
  LegalRepresentativeForm,
  MainUserForm,
  validationSchema
} from './forms'

export const CompanyEdit = () => {
  const { state } = useNavigation()
  const { id } = useParams()
  const submit = useSubmit()

  const { data: { company: { user, legalRepresentative, address, ...currentCompany } } = {} } = useGetCompanyQuery(id)

  const { hasAccess } = useAuthorization({
    redirection: {
      enable: true
    }
  })

  const initialValues = useMemo(() => {
    const values = {
      id: currentCompany.id,
      document_number: currentCompany.documentNumber,
      name: currentCompany.name,
      activity: currentCompany.activity,
      service_area_ids: currentCompany.serviceAreas.map(({ id }) => id),
      email: currentCompany.email,
      phone: currentCompany.phone,
      trade_name: currentCompany.tradeName,
      business_line: currentCompany.businessLine,
      control_type: currentCompany.controlType ?? '',
      other_contacts: currentCompany.otherContacts ?? '',
      legal_representative_attributes: {
        full_name: legalRepresentative.fullName,
        document_number: legalRepresentative.documentNumber,
        email: legalRepresentative.email,
        phone: legalRepresentative.phone
      },
      user_attributes: {
        id: user.id,
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email
      },
      size: currentCompany.size,
      company_service_types: currentCompany.serviceTypes?.map(st => ({
        company_service_type_id: st.id,
        account_service_type_id: st.account_service_type_id
      })) ?? [],
      safety_information: currentCompany.safetyInformation ?? {
        mutuality_id: null,
        safety_manager: {
          full_name: '',
          document_number: '',
          email: '',
          phone: ''
        }
      }
    }

    if (address?.state?.id) {
      values.address_attributes = {
        state_id: address?.state?.id,
        street: address?.street
      }
    }

    return values
  }, [currentCompany])

  const { t } = useTranslation()

  useEffect(() => {
    hasAccess('company#edit')
  }, [])

  const handleStatusChange = async () => {
    if (
      await confirm({
        title: t(`modal.confirm.status.${currentCompany.status}.title`),
        description: t(`modal.confirm.status.${currentCompany.status}.message`)
      })
    ) {
      const params = {
        id: currentCompany.id,
        status: currentCompany.status === 'active' ? 'inactive' : 'active'
      }

      submit(params, { method: 'patch', encType: 'application/json' })
    }
  }

  const handleAccreditedChange = async () => {
    if (
      await confirm({
        title: t(`modal.confirm.accredited.${currentCompany.accredited}.title`),
        description: t(
          `modal.confirm.accredited.${currentCompany.accredited}.message`
        )
      })
    ) {
      const params = {
        id: currentCompany.id,
        accredited: currentCompany.accredited === 'active' ? 'inactive' : 'active'
      }
      submit(params, { method: 'patch', encType: 'application/json' })
    }
  }

  const transformSubmitData = (values) => {
    const {
      company_service_types,
      mutuality_id, // <- limpiamos del payload final
      ...rest
    } = values

    return {
      ...rest,
      company_service_types_attributes: company_service_types
        ?.filter(item => item.company_service_type_id || !item._destroy)
        .map((item) => ({
          ...(item.company_service_type_id && { id: item.company_service_type_id }),
          account_service_type_id: item.account_service_type_id,
          ...(item._destroy && { _destroy: true })
        }))
    }
  }

  const onSubmit = (params) => {
    const transformed = transformSubmitData(params)
    submit(transformed, { method: 'post', encType: 'application/json' })
  }

  return (
    <PageLayout
      title={t('actions.edit.with', {
        what: `${t('company.one').toLowerCase()}`
      })}
      rightSection={
        <>
          <HasAccess permissions='company#activate'>
            <StatusSwitcher
              label={'company'}
              type={'status'}
              value={currentCompany.status}
              onChange={handleStatusChange}
            />
          </HasAccess>
          <HasAccess permissions='company#accredit'>
            <StatusSwitcher
              label={'company'}
              type={'accredited'}
              value={currentCompany.accredited}
              onChange={handleAccreditedChange}
            />
          </HasAccess>
        </>
      }
    >
      <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema}
      >
        {() => (
          <Form>
            <LegalInformationForm />
            <SafetyInformationForm />
            <LegalRepresentativeForm />
            <MainUserForm />
            <CommercialInformationForm />

            <div className='flex justify-end gap-3 mt-10'>
              <Link to={list('companies')}>
                <Button variant='text' type='submit'>
                  {t('actions.cancel')}
                </Button>
              </Link>
              <HasAccess permissions='company#update'>
                <Button loading={state === 'submitting'} type='submit'>{t('actions.update.one')}</Button>
              </HasAccess>
            </div>
          </Form>
        )}
      </Formik>
    </PageLayout>
  )
}